/**
 * Hook to manage indicators and questions for the dashboard, including actions such as creating,
 * updating, removing questions, loading indicators, and setting filters.
 *
 * @module useIndicators
 * @category Hooks
 * @returns {object} The hook exposes several methods for managing indicators and questions.
 */

import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from 'src/redux/index'
import { useAlert } from 'src/hooks/useAlert'
import useLoading from 'src/hooks/useLoading'
import {
  IFilterIndicators,
  IIndicator,
  IIndicatorCreate,
  IPayloadCopyQuestion,
  IPayloadRemoveQuestion,
  IPayloadUpdateQuestion,
  IResponseGetIndicator,
} from 'src/services/dashboard/indicator/indicator.interface'
import {
  copyQuestions,
  createIndicator,
  getIndicator,
  getIndicators,
  removeQuestion,
  updateQuestion,
} from 'src/services/dashboard/indicator/indicator.service'
import {
  setIndicatorCreate,
  setIndicator,
  setFilter,
  resetFilters,
} from 'src/redux/Dashboard/Indicators/IndicatorsSlice'

/**
 * Custom hook to manage indicators for the dashboard, handle actions such as loading, creating,
 * updating, copying, and removing questions related to the indicators.
 * It provides methods for interacting with the Redux store, showing alerts, and managing loading states.
 *
 * @returns {{
 *  filter: IFilterIndicators,
 *  indicatorCreate: IIndicatorCreate,
 *  responseLoadIndicator: IResponseGetIndicator | null,
 *  indicators: IIndicator[],
 *  indicator: IIndicator | null,
 *  copyQuestionsIndicator: (questions: IPayloadCopyQuestion) => void,
 *  updateIndicator: (indicator: IIndicator) => void,
 *  loadIndicator: ({
 *    indicatorId,
 *    currentPage,
 *    pageSize
 *  }: { indicatorId: string, currentPage?: number | null, pageSize?: number | null }) => Promise<void>,
 *  setfilterIndicators: (filter: IFilterIndicators) => void,
 *  loadIndicators: (pagination: {
 *    currentPage?: number | null,
 *    pageSize?: number | null,
 *    areaName?: string | null,
 *    indicatorId?: number | null,
 *    periodId?: number | null,
 *    quarterId?: number | null,
 *    channelId?: number | null
 *  }) => void,
 *  createNewIndicator: (indicator: IIndicatorCreate) => void,
 *  updateIndicatorCreate: (indicator: IIndicatorCreate) => void,
 *  resetFiltersIndicators: () => void,
 *  updateQuestionIndicator: (question: IPayloadUpdateQuestion) => void,
 *  removeQuestionsIndicator: (question: IPayloadRemoveQuestion[]) => void
 * }}
 */
export const useIndicators = () => {
  const { showAlert } = useAlert()
  const { startLoading, stopLoading } = useLoading()
  const dispatch = useDispatch<AppDispatch>()
  const {
    indicatorCreate,
    indicator,
    filter,
    indicators,
    responseLoadIndicator,
  } = useSelector((state: RootState) => state.indicators)

  /**
   * Creates a new indicator and loads the updated list of indicators.
   * @param {IIndicatorCreate} indicator - The indicator to create.
   */
  const createNewIndicator = (indicator: IIndicatorCreate) => {
    try {
      startLoading()
      dispatch(createIndicator(indicator))
        .then((result: any) => {
          if (createIndicator.rejected.match(result)) {
            showAlert('error', String(result.payload) || 'Error al cargar las preguntas')
          } else if (createIndicator.fulfilled.match(result)) {
            loadIndicators({ currentPage: 1, pageSize: 25 })
            showAlert('success', 'Indicador creado correctamente')
          }
        })
        .finally(() => stopLoading())
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    }
  }

  /**
   * Loads a specific indicator by its ID.
   * @param {{ indicatorId: string, currentPage?: number | null, pageSize?: number | null }} params - The ID of the indicator to load and optional pagination parameters.
   * @returns {Promise<void>}
   */
  const loadIndicator = async ({
    indicatorId,
    currentPage,
    pageSize,
  }: {
    indicatorId: string
    currentPage?: number | null
    pageSize?: number | null
  }) => {
    try {
      startLoading()
      dispatch(getIndicator({ indicatorId, currentPage, pageSize })).then(
        (result: any) => {
          if (getIndicator.rejected.match(result)) {
            showAlert('error', String(result.payload) || 'Error al cargar las preguntas')
          } else if (getIndicator.fulfilled.match(result)) {
            showAlert('success', 'Indicador cargado correctamente')
          }
        },
      )
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    } finally {
      stopLoading()
    }
  }

  /**
   * Updates the state of a specific indicator.
   * @param {IIndicator} indicator - The indicator to update.
   */
  const updateIndicator = (indicator: IIndicator) => {
    dispatch(setIndicator(indicator))
  }

  /**
   * Updates the creation state of an indicator.
   * @param {IIndicatorCreate} indicator - The indicator creation data to update.
   */
  const updateIndicatorCreate = (indicator: IIndicatorCreate) => {
    dispatch(setIndicatorCreate(indicator))
  }

  /**
   * Sets the filter criteria for the indicators.
   * @param {IFilterIndicators} filter - The filter criteria to apply.
   */
  const setfilterIndicators = (filter: IFilterIndicators) => {
    dispatch(setFilter(filter))
  }

  /**
   * Resets the applied filters for the indicators.
   */
  const resetFiltersIndicators = () => {
    dispatch(resetFilters())
  }

  /**
   * Loads the list of indicators based on pagination parameters.
   * @param {{ currentPage?: number | null, pageSize?: number | null, areaName?: string | null, indicatorId?: number | null, periodId?: number | null, quarterId?: number | null, channelId?: number | null }} pagination - Pagination parameters for loading the indicators.
   */
  const loadIndicators = (pagination: {
    currentPage?: number | null
    pageSize?: number | null
    areaName?: string | null
    indicatorId?: number | null
    periodId?: number | null
    quarterId?: number | null
    channelId?: number | null
  }) => {
    try {
      startLoading()
      dispatch(getIndicators(pagination))
        .then((result: any) => {
          if (getIndicators.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al cargar las estrategias',
            )
          } else if (getIndicators.fulfilled.match(result)) {
            showAlert('success', 'Estrategias cargadas correctamente')
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    }
  }

  /**
   * Updates the questions associated with an indicator.
   * @param {IPayloadUpdateQuestion} question - The question data to update.
   */
  const updateQuestionIndicator = (question: IPayloadUpdateQuestion) => {
    try {
      startLoading()
      dispatch(updateQuestion(question))
        .then((result: any) => {
          if (updateQuestion.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al cargar las preguntas',
            )
          } else if (updateQuestion.fulfilled.match(result)) {
            loadIndicator({
              indicatorId: String(question.indicatorId),
              currentPage: 1,
              pageSize: 25,
            })
            showAlert('success', 'Indicador creado correctamente')
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    }
  }

  /**
   * Copies questions from one indicator to another.
   * @param {IPayloadCopyQuestion} questions - The question data to copy.
   */
  const copyQuestionsIndicator = (questions: IPayloadCopyQuestion) => {
    try {
      startLoading()
      dispatch(copyQuestions(questions))
        .then((result: any) => {
          if (copyQuestions.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al copiar las preguntas',
            )
          } else if (copyQuestions.fulfilled.match(result)) {
            loadIndicator({
              indicatorId: String(questions.copyFrom.indicatorId),
              currentPage: 1,
              pageSize: 25,
            })
            showAlert('success', 'Preguntas copiadas correctamente')
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    }
  }

  /**
   * Removes questions from an indicator.
   * @param {IPayloadRemoveQuestion[]} question - The questions to remove.
   */
  const removeQuestionsIndicator = (question: IPayloadRemoveQuestion[]) => {
    try {
      startLoading()
      dispatch(removeQuestion(question))
        .then((result: any) => {
          if (removeQuestion.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al eliminar las preguntas',
            )
          } else if (removeQuestion.fulfilled.match(result)) {
            loadIndicator({
              indicatorId: String(question[0].indicatorId),
              currentPage: 1,
              pageSize: 25,
            })
            showAlert('success', 'Pregunta eliminada correctamente')
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    }
  }

  return {
    filter,
    indicatorCreate,
    responseLoadIndicator,
    indicators,
    indicator,
    copyQuestionsIndicator,
    updateIndicator,
    loadIndicator,
    setfilterIndicators,
    loadIndicators,
    createNewIndicator,
    updateIndicatorCreate,
    resetFiltersIndicators,
    updateQuestionIndicator,
    removeQuestionsIndicator,
  }
}

export default useIndicators
